import React, { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Wrench, X, ChevronDown, Plus, Check } from "lucide-react";
import { JobPhotosManager } from "@/pages/admin/JobPhotos";

const ServicesMultiSelect = ({ apptId, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggle = (title) =>
    onChange(selected.includes(title) ? selected.filter((s) => s !== title) : [...selected, title]);

  const addCustom = () => {
    const t = custom.trim();
    if (!t) return;
    if (!selected.includes(t)) onChange([...selected, t]);
    setCustom("");
  };

  return (
    <div ref={ref} className="relative">
      <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Services performed</label>
      <button
        type="button"
        data-testid={`service-record-services-toggle-${apptId}`}
        onClick={() => setOpen(!open)}
        className="mt-1 w-full flex items-center justify-between bg-black border border-zinc-800 p-2.5 text-sm text-zinc-200 hover:border-zinc-600 focus:border-brand-red outline-none"
      >
        <span className={selected.length ? "text-zinc-200" : "text-zinc-600"}>
          {selected.length ? `${selected.length} selected` : "Select services…"}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {selected.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selected.map((s) => (
            <span key={s} data-testid={`service-record-chip-${apptId}`} className="inline-flex items-center gap-1 px-2 py-1 border border-zinc-700 text-[10px] font-mono uppercase tracking-wider text-zinc-300">
              {s}
              <button type="button" onClick={() => toggle(s)} aria-label={`Remove ${s}`} className="text-zinc-500 hover:text-brand-red"><X className="h-2.5 w-2.5" /></button>
            </span>
          ))}
        </div>
      )}
      {open && (
        <div data-testid={`service-record-services-menu-${apptId}`} className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-zinc-950 border border-zinc-700 shadow-xl">
          {options.map((o) => {
            const on = selected.includes(o);
            return (
              <button
                key={o}
                type="button"
                data-testid={`service-record-option-${apptId}-${o.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => toggle(o)}
                className={`w-full flex items-center justify-between px-3 py-2 text-left text-xs ${on ? "text-brand-red" : "text-zinc-300"} hover:bg-zinc-900`}
              >
                {o}
                {on && <Check className="h-3 w-3" />}
              </button>
            );
          })}
          <div className="flex items-center gap-2 border-t border-zinc-800 p-2">
            <input
              data-testid={`service-record-custom-${apptId}`}
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
              placeholder="Add another service…"
              className="flex-1 bg-black border border-zinc-800 px-2 py-1.5 text-xs text-zinc-200 focus:border-brand-red outline-none"
            />
            <button type="button" data-testid={`service-record-custom-add-${apptId}`} onClick={addCustom}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-brand-red text-white text-[10px] font-mono uppercase tracking-widest hover:bg-[#B91C2B]">
              <Plus className="h-3 w-3" /> Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export function ServiceRecordButton({ appt, onSaved, services = [] }) {
  const [open, setOpen] = useState(false);
  const [work, setWork] = useState(appt.work_done || "");
  const [price, setPrice] = useState(appt.price_charged ?? "");
  const [months, setMonths] = useState(appt.next_service_months || 6);
  const [performed, setPerformed] = useState(appt.services_performed || []);
  const [odo, setOdo] = useState(appt.odometer_km ?? "");
  const [nextEnabled, setNextEnabled] = useState(appt.next_service_enabled !== false);
  const [priceTouched, setPriceTouched] = useState(appt.price_charged != null);
  const [busy, setBusy] = useState(false);

  const nextKm = odo !== "" && !isNaN(Number(odo)) ? Number(odo) + 10000 : null;

  // Selecting services prefills price (sum of "from" prices) and appends their descriptions to the notes
  const handlePerformedChange = (next) => {
    const added = next.filter((t) => !performed.includes(t));
    setPerformed(next);
    if (!priceTouched) {
      const total = next.reduce((sum, t) => {
        const svc = services.find((s) => s.title === t);
        return sum + (svc?.price_from ? Number(svc.price_from) : 0);
      }, 0);
      setPrice(total > 0 ? total : "");
    }
    if (added.length) {
      const lines = added
        .map((t) => services.find((s) => s.title === t)?.description)
        .filter(Boolean);
      if (lines.length) {
        setWork((w) => [w.trim(), ...lines].filter(Boolean).join("\n"));
      }
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/appointments/${appt.id}/service-record`, {
        work_done: work,
        price_charged: price === "" ? null : Number(price),
        next_service_months: Number(months),
        next_service_enabled: nextEnabled,
        services_performed: performed,
        odometer_km: odo === "" ? null : Number(odo),
      });
      toast.success("Job record saved — customer can see it in their account");
      setOpen(false);
      onSaved?.();
    } catch {
      toast.error("Could not save job record");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid={`service-record-btn-${appt.id}`}
        onClick={() => setOpen(true)}
        className={`mt-1.5 inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border transition-colors ${appt.work_done ? "border-green-700 text-green-400" : "border-zinc-700 text-zinc-400 hover:border-brand-red hover:text-brand-red"}`}
        title="Record work done, price charged and next service interval"
      >
        <Wrench className="h-3 w-3" /> {appt.work_done ? "Job recorded" : "Job record"}
      </button>
      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-zinc-950 border border-zinc-800 p-6" onClick={(e) => e.stopPropagation()} data-testid={`service-record-modal-${appt.id}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-display uppercase text-lg text-white tracking-tight">Job record — {appt.user_name}</h3>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <p className="mt-1 text-xs text-zinc-500">{appt.vehicle_make} {appt.vehicle_model} · {appt.preferred_date}</p>

            <div className="mt-4">
              <ServicesMultiSelect
                apptId={appt.id}
                options={services.map((s) => s.title)}
                selected={performed}
                onChange={handlePerformedChange}
              />
            </div>

            <textarea
              data-testid={`service-record-work-${appt.id}`}
              rows={3}
              value={work}
              onChange={(e) => setWork(e.target.value)}
              placeholder="Extra notes — e.g. machined rotors, wheel alignment, replaced wiper blades…"
              className="mt-3 w-full bg-black border border-zinc-800 p-3 text-sm text-zinc-200 focus:border-brand-red outline-none resize-none"
            />

            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Price charged ($)</label>
                <input
                  data-testid={`service-record-price-${appt.id}`}
                  type="number" min="0" step="0.01" value={price}
                  onChange={(e) => { setPrice(e.target.value); setPriceTouched(true); }}
                  className="mt-1 w-full bg-black border border-zinc-800 p-2.5 text-sm text-zinc-200 focus:border-brand-red outline-none"
                />
                {!priceTouched && performed.length > 0 && price !== "" && (
                  <p className="mt-1 text-[10px] text-zinc-600">Auto-summed from service "from" prices — edit to override.</p>
                )}
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Odometer (km)</label>
                <input
                  data-testid={`service-record-odo-${appt.id}`}
                  type="number" min="0" step="1" value={odo}
                  onChange={(e) => setOdo(e.target.value)}
                  placeholder="e.g. 84000"
                  className="mt-1 w-full bg-black border border-zinc-800 p-2.5 text-sm text-zinc-200 focus:border-brand-red outline-none"
                />
              </div>
            </div>

            <div className="mt-4 border border-zinc-900 bg-black/40 p-3">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  data-testid={`service-record-next-toggle-${appt.id}`}
                  type="checkbox"
                  checked={nextEnabled}
                  onChange={(e) => setNextEnabled(e.target.checked)}
                  className="h-4 w-4 accent-[#E11D2B]"
                />
                <span className="text-xs text-zinc-300 font-semibold">Schedule next service</span>
                <span className="text-[10px] text-zinc-600">(untick for one-off jobs)</span>
              </label>
              {nextEnabled && (
                <div className="mt-3">
                  <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Next service in</label>
                  <select
                    data-testid={`service-record-months-${appt.id}`}
                    value={months}
                    onChange={(e) => setMonths(e.target.value)}
                    className="mt-1 w-full bg-black border border-zinc-800 p-2.5 text-sm text-zinc-200 focus:border-brand-red outline-none"
                  >
                    {[3, 6, 9, 12, 18, 24].map((m) => <option key={m} value={m}>{`${m} months`}</option>)}
                  </select>
                  <p data-testid={`service-record-next-summary-${appt.id}`} className="mt-2 text-[11px] text-zinc-500">
                    Next service due in {months} months{nextKm ? <> or at <span className="text-zinc-300">{nextKm.toLocaleString()} km</span> (odometer + 10,000 km)</> : ""}.
                    The customer gets an SMS + email reminder 2 weeks before it's due.
                  </p>
                </div>
              )}
              {!nextEnabled && (
                <p className="mt-2 text-[11px] text-zinc-600">No next-service date will be set and no reminder will be sent for this job.</p>
              )}
            </div>

            <div className="mt-4 border-t border-zinc-900 pt-4">
              <JobPhotosManager appt={appt} />
            </div>
            <button
              data-testid={`service-record-save-${appt.id}`}
              onClick={save} disabled={busy}
              className="mt-4 w-full px-4 py-3 bg-brand-red text-white text-xs font-mono uppercase tracking-widest hover:bg-[#B91C2B] disabled:opacity-60"
            >
              {busy ? "Saving…" : "Save job record"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
